import React, { useEffect, useState } from 'react'
import { templateDataCourse } from '../course'
// Import the styles function
import styles from 'src/components/editor/components/ck-editor/styles'
import { withStyles } from '@material-ui/core/styles' // or your preferred styling library

// This component renders the course landing page template with the selected data
const TemplateRenderer = ({
  selectedTemplate,
  courseData,
  fontSize = null,
  fontFamily = 'Inter',
  page = 'landing-page',
  classes,
}) => {
  const [template, setTemplate] = useState(null)

  useEffect(() => {
    // Find the selected template from templateDataCourse based on ID or default to first one
    const foundTemplate =
      templateDataCourse.find(template => template.id === selectedTemplate) ||
      templateDataCourse[0]

    setTemplate(foundTemplate)
  }, [selectedTemplate])

  // Function to distribute audience items across multiple UL elements
  const distributeAudienceItems = (audienceItems, modifiedHtml) => {
    if (!audienceItems || !audienceItems.length) return modifiedHtml

    // Find all UL elements in the audience section
    const audienceSectionMatch = modifiedHtml.match(
      /<div[^>]*class="simple-box-wrapper"[^>]*>[\s\S]*?id="audience-title"[\s\S]*?<\/div>\s*<\/div>\s*<\/div>/,
    )
    if (!audienceSectionMatch) return modifiedHtml

    const audienceSection = audienceSectionMatch[0]

    // Find all UL elements in the audience section
    const ulElements = []
    let ulMatch
    const ulRegex = /<ul[^>]*>([\s\S]*?)<\/ul>/g

    while ((ulMatch = ulRegex.exec(audienceSection)) !== null) {
      ulElements.push(ulMatch[0])
    }

    // If we don't have enough UL elements, we'll need to add them
    if (ulElements.length === 0) return modifiedHtml

    // Extract template for list items to preserve styling
    let itemTemplate = '<li>${item}</li>' // Default template
    const listItemMatch = audienceSection.match(/<li>.*?<\/li>/s)

    if (listItemMatch) {
      const listItemTemplate = listItemMatch[0]
      // Check if list item has spans with styling
      if (listItemTemplate.includes('<span')) {
        // Create template maintaining span styling
        const spanMatch = listItemTemplate.match(
          /(<li>)(<span[^>]*>).*?(<\/span>)(<\/li>)/s,
        )
        if (spanMatch) {
          itemTemplate = `${spanMatch[1]}${spanMatch[2]}\${item}${spanMatch[3]}${spanMatch[4]}`
        }
      }
    }

    // Calculate how many items per UL
    const itemCount = audienceItems.length
    const ulCount = ulElements.length

    // If there's only one UL, just update it with all items
    if (ulCount === 1) {
      const allItemsHtml = audienceItems
        .map(item => itemTemplate.replace('${item}', item))
        .join('')

      // Preserve the ul styling
      const ulMatch = modifiedHtml.match(
        /(<ul[^>]*id="audience-items"[^>]*>)[\s\S]*?<\/ul>/,
      )
      if (ulMatch) {
        return modifiedHtml.replace(
          /(<ul[^>]*id="audience-items"[^>]*>)[\s\S]*?<\/ul>/,
          `${ulMatch[1]}${allItemsHtml}</ul>`,
        )
      } else {
        return modifiedHtml.replace(
          /<ul[^>]*id="audience-items"[^>]*>[\s\S]*?<\/ul>/,
          `<ul id="audience-items">${allItemsHtml}</ul>`,
        )
      }
    }

    // For multiple ULs, distribute items evenly
    const itemsPerUl = Math.ceil(itemCount / ulCount)
    let updatedHtml = modifiedHtml

    // First, update the UL with id="audience-items"
    const firstUlItems = audienceItems.slice(0, itemsPerUl)
    const firstUlHtml = firstUlItems
      .map(item => itemTemplate.replace('${item}', item))
      .join('')

    // Preserve the first ul styling
    const firstUlMatch = updatedHtml.match(
      /(<ul[^>]*id="audience-items"[^>]*>)[\s\S]*?<\/ul>/,
    )
    if (firstUlMatch) {
      updatedHtml = updatedHtml.replace(
        /(<ul[^>]*id="audience-items"[^>]*>)[\s\S]*?<\/ul>/,
        `${firstUlMatch[1]}${firstUlHtml}</ul>`,
      )
    } else {
      updatedHtml = updatedHtml.replace(
        /<ul[^>]*id="audience-items"[^>]*>[\s\S]*?<\/ul>/,
        `<ul id="audience-items">${firstUlHtml}</ul>`,
      )
    }

    // Now update the remaining ULs
    let remainingItems = audienceItems.slice(itemsPerUl)

    // Find the other ULs (without the audience-items id)
    const otherUlRegex = /<ul(?![^>]*id="audience-items")[^>]*>([\s\S]*?)<\/ul>/g

    let otherUlMatch
    let ulIndex = 0

    while (
      (otherUlMatch = otherUlRegex.exec(updatedHtml)) !== null &&
      remainingItems.length > 0
    ) {
      const currentUl = otherUlMatch[0]
      const itemsForThisUl = remainingItems.slice(0, itemsPerUl)
      const newUlHtml = itemsForThisUl
        .map(item => itemTemplate.replace('${item}', item))
        .join('')

      // Capture original ul tag with attributes
      const ulTagMatch = currentUl.match(/(<ul[^>]*>)([\s\S]*?)(<\/ul>)/)
      if (ulTagMatch) {
        updatedHtml = updatedHtml.replace(
          currentUl,
          `${ulTagMatch[1]}${newUlHtml}${ulTagMatch[3]}`,
        )
      } else {
        updatedHtml = updatedHtml.replace(currentUl, `<ul>${newUlHtml}</ul>`)
      }

      remainingItems = remainingItems.slice(itemsPerUl)
      ulIndex++

      // Reset lastIndex to ensure we continue searching from the beginning
      // This is necessary because we're modifying the string as we go
      otherUlRegex.lastIndex = 0
    }

    return updatedHtml
  }

  // Map the courseData to the template IDs
  const mapDataToTemplate = html => {
    if (!html || !courseData) return html

    let modifiedHtml = html

    // Process introduction section
    if (courseData.introduction) {
      // Replace title content
      const introTitleMatch = modifiedHtml.match(
        /<span[^>]*id="introduction-title"[^>]*>[^<]*<\/span>/,
      )
      if (introTitleMatch) {
        const introTitleTag = introTitleMatch[0]
        const newIntroTitleTag = introTitleTag.replace(
          />([^<]*)<\/span>/,
          `>${courseData.introduction.title || ''}</span>`,
        )
        modifiedHtml = modifiedHtml.replace(introTitleTag, newIntroTitleTag)
      }

      // Replace description content
      const introDescMatch = modifiedHtml.match(
        /<p[^>]*id="introduction-description"[^>]*>[^<]*<\/p>/,
      )
      if (introDescMatch) {
        const introDescTag = introDescMatch[0]
        const newIntroDescTag = introDescTag.replace(
          />([^<]*)<\/p>/,
          `>${courseData.introduction.text || ''}</p>`,
        )
        modifiedHtml = modifiedHtml.replace(introDescTag, newIntroDescTag)
      } else {
        // If the p tag has no closing tag on the same line, try another approach
        modifiedHtml = modifiedHtml.replace(
          /<p[^>]*id="introduction-description"[^>]*>.*?(?=<\/p>)/s,
          `<p id="introduction-description">${
            courseData.introduction.text || ''
          }`,
        )
      }
    }

    // Process author section
    if (courseData.author) {
      // Replace title content
      const authorTitleMatch = modifiedHtml.match(
        /<span[^>]*id="author-title"[^>]*>[^<]*<\/span>/,
      )
      if (authorTitleMatch) {
        const authorTitleTag = authorTitleMatch[0]
        const newAuthorTitleTag = authorTitleTag.replace(
          />([^<]*)<\/span>/,
          `>${courseData.author.title || ''}</span>`,
        )
        modifiedHtml = modifiedHtml.replace(authorTitleTag, newAuthorTitleTag)
      }

      // Replace description content
      const authorDescMatch = modifiedHtml.match(
        /<p[^>]*id="author-description"[^>]*>.*?<\/p>/s,
      )
      if (authorDescMatch) {
        const authorDescTag = authorDescMatch[0]
        // Preserve span tags if they exist
        if (authorDescTag.includes('<span')) {
          // FIXED: Better regex to preserve span attributes while replacing text
          const newAuthorDescTag = authorDescTag.replace(
            /(<span[^>]*>).*?(<\/span>)/s,
            `$1${courseData.author.text || ''}$2`,
          )
          modifiedHtml = modifiedHtml.replace(authorDescTag, newAuthorDescTag)
        } else {
          const newAuthorDescTag = authorDescTag.replace(
            />([^<]*)<\/p>/,
            `>${courseData.author.text || ''}</p>`,
          )
          modifiedHtml = modifiedHtml.replace(authorDescTag, newAuthorDescTag)
        }
      }
    }

    // Process certificate/badge section
    if (courseData.certificate || courseData.badge) {
      const certificateData = courseData.certificate || courseData.badge

      // Replace title content
      const certTitleMatch = modifiedHtml.match(
        /<span[^>]*id="certificate-title"[^>]*>[^<]*<\/span>/,
      )
      if (certTitleMatch) {
        const certTitleTag = certTitleMatch[0]
        const newCertTitleTag = certTitleTag.replace(
          />([^<]*)<\/span>/,
          `>${certificateData.title || ''}</span>`,
        )
        modifiedHtml = modifiedHtml.replace(certTitleTag, newCertTitleTag)
      }

      // Replace description content with more precise regex
      const certDescMatch = modifiedHtml.match(
        /<p[^>]*id="certificate-description"[^>]*>.*?<\/p>/s,
      )
      if (certDescMatch) {
        const certDescTag = certDescMatch[0]
        // FIXED: Preserve any formatting inside the paragraph
        if (certDescTag.includes('<span')) {
          const newCertDescTag = certDescTag.replace(
            /(<span[^>]*>).*?(<\/span>)/s,
            `$1${certificateData.text || ''}$2`,
          )
          modifiedHtml = modifiedHtml.replace(certDescTag, newCertDescTag)
        } else {
          // Handle multiline content with a more specific replacement
          modifiedHtml = modifiedHtml.replace(
            /<p[^>]*id="certificate-description"[^>]*>.*?<\/p>/s,
            `<p id="certificate-description">${certificateData.text || ''}</p>`,
          )
        }
      }
    }

    // Process program details section
    if (courseData.program_details) {
      // Replace title content
      const programTitleMatch = modifiedHtml.match(
        /<span[^>]*id="program_details-title"[^>]*>[^<]*<\/span>/,
      )
      if (programTitleMatch) {
        const programTitleTag = programTitleMatch[0]
        const newProgramTitleTag = programTitleTag.replace(
          />([^<]*)<\/span>/,
          `>${courseData.program_details.title || ''}</span>`,
        )
        modifiedHtml = modifiedHtml.replace(programTitleTag, newProgramTitleTag)
      }

      // Replace description content
      const programDescMatch = modifiedHtml.match(
        /<p[^>]*id="program_details-description"[^>]*>.*?<\/p>/s,
      )
      if (programDescMatch) {
        const programDescTag = programDescMatch[0]
        // Preserve span tags if they exist
        if (programDescTag.includes('<span')) {
          // FIXED: Better regex to preserve span attributes
          const newProgramDescTag = programDescTag.replace(
            /(<span[^>]*>).*?(<\/span>)/s,
            `$1${courseData.program_details.text || ''}$2`,
          )
          modifiedHtml = modifiedHtml.replace(programDescTag, newProgramDescTag)
        } else {
          const newProgramDescTag = programDescTag.replace(
            />([^<]*)<\/p>/,
            `>${courseData.program_details.text || ''}</p>`,
          )
          modifiedHtml = modifiedHtml.replace(programDescTag, newProgramDescTag)
        }
      }

      // Handle program details items if they exist
      if (courseData.program_details.items) {
        const {
          level,
          duration,
          recommended_interval,
          price,
        } = courseData.program_details.items

        // Use ID selectors with more reliable regex patterns
        if (level) {
          modifiedHtml = modifiedHtml.replace(
            /<p[^>]*id="level"[^>]*>.*?<\/p>/s,
            `<p id="level">${level}</p>`,
          )
        }

        if (duration) {
          modifiedHtml = modifiedHtml.replace(
            /<p[^>]*id="duration"[^>]*>.*?<\/p>/s,
            `<p id="duration">${duration}</p>`,
          )
        }

        if (recommended_interval) {
          modifiedHtml = modifiedHtml.replace(
            /<p[^>]*id="recommended_interval"[^>]*>.*?<\/p>/s,
            `<p id="recommended_interval">${recommended_interval}</p>`,
          )
        }

        if (price) {
          modifiedHtml = modifiedHtml.replace(
            /<p[^>]*id="price"[^>]*>.*?<\/p>/s,
            `<p id="price">${price}</p>`,
          )
        }

        // Fallback approach in case ID-based replacement doesn't work
        // This uses the icon context to identify the correct paragraphs
        const iconMap = {
          'analytics-03': level,
          'clock-01': recommended_interval,
          'book-02': duration,
          'dollar-circle': price,
        }

        // Replace content using icon identifiers as context
        for (const [icon, value] of Object.entries(iconMap)) {
          if (value) {
            const iconPattern = new RegExp(
              `icon="${icon}"[\\s\\S]*?<div class="card-content">\\s*<p[^>]*>.*?<\\/p>`,
              's',
            )
            const iconMatch = modifiedHtml.match(iconPattern)

            if (iconMatch) {
              const replacedContent = iconMatch[0].replace(
                /(<p[^>]*>).*?(<\/p>)/s,
                `$1${value}$2`,
              )
              modifiedHtml = modifiedHtml.replace(iconMatch[0], replacedContent)
            }
          }
        }
      }
    }

    // Process audience section with the new distribution function
    if (courseData.audience) {
      // Replace title content
      const audienceTitleMatch = modifiedHtml.match(
        /<span[^>]*id="audience-title"[^>]*>[^<]*<\/span>/,
      )
      if (audienceTitleMatch) {
        const audienceTitleTag = audienceTitleMatch[0]
        const newAudienceTitleTag = audienceTitleTag.replace(
          />([^<]*)<\/span>/,
          `>${courseData.audience.title || ''}</span>`,
        )
        modifiedHtml = modifiedHtml.replace(
          audienceTitleTag,
          newAudienceTitleTag,
        )
      }

      // Replace description content
      const audienceDescMatch = modifiedHtml.match(
        /<p[^>]*id="audience-description"[^>]*>.*?<\/p>/s,
      )
      if (audienceDescMatch) {
        const audienceDescTag = audienceDescMatch[0]
        // Preserve span tags if they exist
        if (audienceDescTag.includes('<span')) {
          // FIXED: Better regex to preserve span attributes
          const newAudienceDescTag = audienceDescTag.replace(
            /(<span[^>]*>).*?(<\/span>)/s,
            `$1${courseData.audience.text || ''}$2`,
          )
          modifiedHtml = modifiedHtml.replace(
            audienceDescTag,
            newAudienceDescTag,
          )
        } else {
          const newAudienceDescTag = audienceDescTag.replace(
            />([^<]*)<\/p>/,
            `>${courseData.audience.text || ''}</p>`,
          )
          modifiedHtml = modifiedHtml.replace(
            audienceDescTag,
            newAudienceDescTag,
          )
        }
      }

      // Handle audience items using our new distribution function
      if (courseData.audience.items && courseData.audience.items.length > 0) {
        modifiedHtml = distributeAudienceItems(
          courseData.audience.items,
          modifiedHtml,
        )
      }
    }

    // Process learning outcomes section
    if (courseData.learning_outcomes) {
      // Replace title content
      const learningTitleMatch = modifiedHtml.match(
        /<span[^>]*id="learning_outcomes-title"[^>]*>[^<]*<\/span>/,
      )
      if (learningTitleMatch) {
        const learningTitleTag = learningTitleMatch[0]
        const newLearningTitleTag = learningTitleTag.replace(
          />([^<]*)<\/span>/,
          `>${courseData.learning_outcomes.title || ''}</span>`,
        )
        modifiedHtml = modifiedHtml.replace(
          learningTitleTag,
          newLearningTitleTag,
        )
      }

      // Replace description content
      const learningDescMatch = modifiedHtml.match(
        /<p[^>]*id="learning_outcomes-description"[^>]*>.*?<\/p>/s,
      )
      if (learningDescMatch) {
        const learningDescTag = learningDescMatch[0]
        // Keep the strong tag if it exists
        if (learningDescTag.includes('<strong>')) {
          // FIXED: Preserve the existing strong tag without hardcoding it
          const newLearningDescTag = learningDescTag.replace(
            /(<strong[^>]*>).*?(<\/strong>)/s,
            `$1${courseData.learning_outcomes.text || ''}$2`,
          )
          modifiedHtml = modifiedHtml.replace(
            learningDescTag,
            newLearningDescTag,
          )
        } else {
          const newLearningDescTag = learningDescTag.replace(
            />([^<]*)<\/p>/,
            `>${courseData.learning_outcomes.text || ''}</p>`,
          )
          modifiedHtml = modifiedHtml.replace(
            learningDescTag,
            newLearningDescTag,
          )
        }
      }

      // Handle learning outcomes items if they exist
      if (
        courseData.learning_outcomes.items &&
        courseData.learning_outcomes.items.length > 0
      ) {
        // Extract template for list items to preserve styling
        const listItemMatch = modifiedHtml.match(/<li>.*?<\/li>/s)
        let itemTemplate = '<li>${item}</li>' // Default template

        if (listItemMatch) {
          const listItemTemplate = listItemMatch[0]
          // Check if list item has spans with styling
          if (listItemTemplate.includes('<span')) {
            // Create template maintaining span styling
            const spanMatch = listItemTemplate.match(
              /(<li>)(<span[^>]*>).*?(<\/span>)(<\/li>)/s,
            )
            if (spanMatch) {
              itemTemplate = `${spanMatch[1]}${spanMatch[2]}\${item}${spanMatch[3]}${spanMatch[4]}`
            }
          }
        }

        // Apply template to all items
        const learningItemsHtml = courseData.learning_outcomes.items
          .map(item => itemTemplate.replace('${item}', item))
          .join('')

        // Preserve the ul styling
        const ulMatch = modifiedHtml.match(
          /(<ul[^>]*id="learning_outcomes-items"[^>]*>)[\s\S]*?<\/ul>/,
        )
        if (ulMatch) {
          modifiedHtml = modifiedHtml.replace(
            /(<ul[^>]*id="learning_outcomes-items"[^>]*>)[\s\S]*?<\/ul>/,
            `${ulMatch[1]}${learningItemsHtml}</ul>`,
          )
        } else {
          // Fallback to simpler replacement
          modifiedHtml = modifiedHtml.replace(
            /id="learning_outcomes-items"[^>]*>([\s\S]*?)<\/ul>/,
            `id="learning_outcomes-items">${learningItemsHtml}</ul>`,
          )
        }
      }
    }

    // Process outro section
    if (courseData.outro) {
      // Replace title content
      const outroTitleMatch = modifiedHtml.match(
        /<span[^>]*id="outro-title"[^>]*>[^<]*<\/span>/,
      )
      if (outroTitleMatch) {
        const outroTitleTag = outroTitleMatch[0]
        const newOutroTitleTag = outroTitleTag.replace(
          />([^<]*)<\/span>/,
          `>${courseData.outro.title || ''}</span>`,
        )
        modifiedHtml = modifiedHtml.replace(outroTitleTag, newOutroTitleTag)
      }

      // Replace description content
      const outroDescMatch = modifiedHtml.match(
        /<p[^>]*id="outro-description"[^>]*>.*?<\/p>/s,
      )
      if (outroDescMatch) {
        const outroDescTag = outroDescMatch[0]
        // Preserve span tags if they exist
        if (outroDescTag.includes('<span')) {
          // FIXED: Better regex to preserve span attributes
          const newOutroDescTag = outroDescTag.replace(
            /(<span[^>]*>).*?(<\/span>)/s,
            `$1${courseData.outro.text || ''}$2`,
          )
          modifiedHtml = modifiedHtml.replace(outroDescTag, newOutroDescTag)
        } else {
          const newOutroDescTag = outroDescTag.replace(
            />([^<]*)<\/p>/,
            `>${courseData.outro.text || ''}</p>`,
          )
          modifiedHtml = modifiedHtml.replace(outroDescTag, newOutroDescTag)
        }
      }
    }

    return modifiedHtml
  }

  if (!template) {
    return <div>Loading template...</div>
  }

  // Find the main content section from the template
  const mainSection = template.value.find(section => section.type === 'Main')
  if (!mainSection) {
    return <div>Template structure is invalid</div>
  }

  const mappedHtml = mapDataToTemplate(mainSection.content.html)

  return (
    <div className={classes.ckEditorFont}>
      {/* Render the main content with the mapped data */}
      <div
        className="main-content"
        dangerouslySetInnerHTML={{ __html: mappedHtml }}
      />
    </div>
  )
}

// Apply the styles to the component
export default withStyles(styles)(TemplateRenderer)
